import { Pipe, PipeTransform } from '@angular/core';
import { applyPrecision, isNumberFinite, isUndefined } from '../helpers/helpers';
import { TranslateService } from '@ngx-translate/core';

/**
 * Renders a byte count as a human-readable size with a translated unit — '1.05 Ko' in French,
 * '1.05 KB' in English.
 *
 * `pure: false` is load-bearing, not a default left in place. The pipe's output depends on the
 * active language as much as on its argument, and a pure pipe is only re-run when its argument
 * changes — so on a language switch Angular would keep serving the value computed in the previous
 * language. This is the same reason ngx-translate declares its own `translate` pipe impure. The
 * body stays a lookup over six entries plus a dictionary read, with no allocation or subscription,
 * so re-running it per change-detection cycle costs what reading a translated string costs
 * anywhere else on the page.
 */
@Pipe({ name: 'bytes', pure: false })
export class BytesPipe implements PipeTransform {

  constructor(public translateService: TranslateService) { }

  /**
   * Readonly on purpose, and the point of this whole file. The previous implementation resolved
   * the unit by ASSIGNING the translation back over the key it had just read, which destroyed the
   * key after a single render: the entry then held 'Ko', looking that up in English returned 'Ko'
   * again (an unknown key resolves to itself), and the unit stayed stuck in whichever language
   * happened to render first. Freezing the array makes that assignment a compile error rather than
   * a defect waiting to be reintroduced.
   */
  private readonly dictionary: ReadonlyArray<{ readonly max: number; readonly unitKey: string }> = [
    { max: 1000, unitKey: 'UNIT_B' },
    { max: 1000000, unitKey: 'UNIT_KB' },
    { max: 1000000000, unitKey: 'UNIT_MB' },
    { max: 1000000000000, unitKey: 'UNIT_GB' },
    { max: 1000000000000000, unitKey: 'UNIT_TB' },
    { max: Infinity, unitKey: 'UNIT_TB' }, // TO FIX don't know what to set here
  ];

  /**
   * @param value The size in bytes.
   * @param precision Number of decimals to keep; the raw quotient when omitted.
   * @returns The formatted size, or null when the value is not a finite number.
   */
  transform(value: number, precision?: number | undefined): string | number {
    if (!isNumberFinite(value)) {
      return null;
    }

    const format = this.dictionary.find(entry => value < entry.max) || this.dictionary[this.dictionary.length - 1];
    const calc = value / (format.max / 1000);
    const num = isUndefined(precision) ? calc : applyPrecision(calc, precision);

    // Resolved synchronously, on every call. The previous code subscribed to the asynchronous
    // `get()` and returned before it could emit whenever the locale file was still in flight, so
    // the view received the raw key - '45.68 UNIT_MB' - and, the pipe being pure at the time, was
    // never re-rendered once the translation arrived. Reading the current value each time means a
    // late bundle and a language change are both picked up with no state to keep in sync.
    return `${num} ${this.translateService.instant(format.unitKey)}`;
  }
}
